

// @ts-ignore
import { initializeApp, getApps, getApp } from 'firebase/app';
// @ts-ignore
import { getAuth, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, onAuthStateChanged, User as FirebaseUser, updateProfile, sendEmailVerification } from 'firebase/auth';
import { getFirestore, collection, addDoc, getDocs, doc, getDoc, setDoc, updateDoc, deleteDoc, increment, orderBy, query, serverTimestamp, arrayUnion, writeBatch } from 'firebase/firestore';
import { getStorage, ref, uploadBytesResumable, getDownloadURL, uploadString } from 'firebase/storage';
import { Booklet, AppSettings, User, Hotspot, Lead } from '../types';

// --- CONFIGURATION ---
const firebaseConfig = {
  apiKey: "AIzaSyD_3FRdlXO9dQEGNXXIRIllqKoOO-2tT8Q",
  authDomain: "esselle-publisher-b3bbf.firebaseapp.com",
  projectId: "esselle-publisher-b3bbf",
  storageBucket: "esselle-publisher-b3bbf.firebasestorage.app",
  messagingSenderId: "958825934813",
  appId: "1:958825934813:web:81d9fe905c96284ab91034"
};

// --- CONSTANTS ---
const LS_USER_KEY = 'esselle_demo_user';
const LS_DATA_KEY = 'esselle_demo_booklets';
const LS_SETTINGS_KEY = 'esselle_demo_settings';
const DEMO_PDF_URL = 'https://raw.githubusercontent.com/mozilla/pdf.js/ba2edeae/web/compressed.tracemonkey-pldi-09.pdf'; // Valid sample PDF for demo

// --- INITIALIZATION STATE ---
let app;
let auth: any = null;
let db: any = null;
let storage: any = null;
let isDemo = false;

try {
    // 1. Initialize App
    // @ts-ignore
    const apps = typeof getApps === 'function' ? getApps() : [];
    
    // @ts-ignore
    app = !apps.length ? initializeApp(firebaseConfig) : (typeof getApp === 'function' ? getApp() : apps[0]);
    
    // 2. Initialize Services
    auth = getAuth(app);
    db = getFirestore(app);
    storage = getStorage(app);
    
    console.log("🔥 Firebase initialized successfully.");
    isDemo = false;
} catch (e) {
    console.warn("⚠️ Firebase initialization failed. Switching to LOCAL DEMO MODE.", e);
    isDemo = true;
}

// Global accessor for UI to know if we are in demo mode
export const isAppInDemoMode = () => isDemo;

// --- LOCAL STORAGE HELPERS (For Demo Mode) ---
const getLocalBooklets = (): Booklet[] => {
    try {
        const str = localStorage.getItem(LS_DATA_KEY);
        return str ? JSON.parse(str) : [];
    } catch { return []; }
};

const setLocalBooklets = (data: Booklet[]) => {
    localStorage.setItem(LS_DATA_KEY, JSON.stringify(data));
};

// --- UTILS ---
const cleanData = (data: any) => {
    const cleaned: any = {};
    Object.keys(data).forEach(key => {
        if (data[key] !== undefined) {
            cleaned[key] = data[key];
        }
    });
    return cleaned;
};

const normalizeDate = (val: any): number => {
    if (!val) return Date.now();
    if (typeof val === 'number') return val;
    if (val.toMillis) return val.toMillis(); // Firestore Timestamp
    if (val instanceof Date) return val.getTime();
    return Date.now();
};

const normalizeOptionalDate = (val: any): number | null | undefined => {
    if (val === null || val === undefined) return val;
    if (typeof val === 'number') return val;
    if (val.toMillis) return val.toMillis();
    if (val instanceof Date) return val.getTime();
    return null;
}

// --- SIGNED-URL SELF-HEAL ------------------------------------------------
// Some booklets were rendered by an older pipeline that stored Google *signed*
// download URLs (storage.googleapis.com/<bucket>/<path>?...&Signature=...).
// Google's managed signing key rotates, which eventually invalidates every one
// of those URLs — they start returning 403 SignatureDoesNotMatch and the page
// image / thumbnail silently fails to load (blank white pages). The underlying
// objects still exist and are publicly readable, so we rewrite any signed URL
// to the permanent Firebase download endpoint at read time. Firebase download
// URLs never rotate, so this fixes it once and cannot recur.
//
// This only touches image/PDF URLs *inside* a booklet — never document IDs,
// routes, or the domain — so existing share links and QR codes (which encode
// only /#/view/<bookletId>) are completely unaffected. Token URLs and any other
// URL shape pass through untouched.
const toPermanentUrl = (url: any): any => {
    if (typeof url !== 'string' || !url) return url;
    if (url.indexOf('firebasestorage.googleapis.com') !== -1) return url; // already a Firebase URL
    const m = url.match(/^https?:\/\/storage\.googleapis\.com\/([^/]+)\/([^?]+)/);
    if (!m) return url;
    // Only rewrite genuinely signed URLs; leave anything else alone.
    if (!/[?&](Signature|GoogleAccessId|X-Goog-Signature)=/.test(url)) return url;
    const bucket = m[1];
    let objectPath = m[2];
    try { objectPath = decodeURIComponent(objectPath); } catch { /* keep as-is */ }
    return `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodeURIComponent(objectPath)}?alt=media`;
};

const toPermanentUrls = (arr: any): any =>
    Array.isArray(arr) ? arr.map(toPermanentUrl) : arr;

// --- LIST CACHE (instant dashboard/library paint on repeat visits) ---
// Stores a lightweight copy of the last fetched booklet list so the UI can
// render immediately while a fresh fetch runs in the background.
const LIST_CACHE_PREFIX = 'esselle_list_cache_';

const stripHeavyFields = (b: Booklet): Booklet => {
    // Never cache page URL arrays, OCR text, or lead emails in the browser
    const { pages, pagesPreview, pagesMobile, pageTexts, collectedLeads, collectedEmails, ...light } = b as any;
    return light as Booklet;
};

export const getCachedBookletList = (publicOnly: boolean): Booklet[] => {
    if (isDemo) return [];
    try {
        const str = localStorage.getItem(LIST_CACHE_PREFIX + (publicOnly ? 'public' : 'all'));
        return str ? JSON.parse(str) : [];
    } catch { return []; }
};

const setCachedBookletList = (publicOnly: boolean, data: Booklet[]) => {
    if (isDemo) return;
    try {
        localStorage.setItem(
            LIST_CACHE_PREFIX + (publicOnly ? 'public' : 'all'),
            JSON.stringify(data.map(stripHeavyFields))
        );
    } catch { /* quota exceeded — cache is optional, ignore */ }
};

// ----- AUTH SERVICE -----

export const subscribeToAuth = (callback: (user: User | null) => void) => {
    // DEMO FALLBACK
    if (isDemo || !auth) {
        const stored = localStorage.getItem(LS_USER_KEY);
        if (stored) {
            callback(JSON.parse(stored));
        } else {
            callback(null);
        }
        // Return a dummy unsubscribe function
        return () => {};
    }

    // REAL FIREBASE
    return onAuthStateChanged(auth, async (user: FirebaseUser | null) => {
        if (user) {
            try { await user.reload(); } catch (e) { callback(null); return; }

            if (!user.emailVerified) {
                await signOut(auth);
                callback(null);
                return;
            }
            
            callback({
                uid: user.uid,
                email: user.email,
                displayName: user.displayName,
                photoURL: user.photoURL,
                emailVerified: true
            });
        } else {
            callback(null);
        }
    });
};

export const loginWithEmail = async (emailInput: string, passwordInput: string) => {
    // DEMO FALLBACK
    if (isDemo || !auth) {
        if (emailInput === 'admin' && passwordInput === 'admin') {
             const fakeUser = { 
                uid: 'demo-admin', 
                email: 'admin@demo.local', 
                displayName: 'Demo Admin', 
                emailVerified: true 
            };
            localStorage.setItem(LS_USER_KEY, JSON.stringify(fakeUser));
            // Trigger a reload to update UI
            window.location.reload(); 
            return fakeUser;
        }
        throw new Error("Demo Mode: Login with 'admin' / 'admin'");
    }

    // REAL FIREBASE
    const cred = await signInWithEmailAndPassword(auth, emailInput, passwordInput);
    await cred.user.reload();
    if (!cred.user.emailVerified) {
        await signOut(auth);
        throw { code: 'auth/email-not-verified', user: cred.user, message: 'Email not verified' };
    }
    return cred.user; 
};

export const registerWithEmail = async (emailInput: string, passwordInput: string, nameInput: string) => {
    // DEMO FALLBACK
    if (isDemo || !auth) {
         const fakeUser = { 
            uid: `demo-${Date.now()}`, 
            email: emailInput, 
            displayName: nameInput, 
            emailVerified: true 
        };
        localStorage.setItem(LS_USER_KEY, JSON.stringify(fakeUser));
        window.location.reload();
        return fakeUser;
    }

    // REAL FIREBASE
    const cred = await createUserWithEmailAndPassword(auth, emailInput, passwordInput);
    await updateProfile(cred.user, { displayName: nameInput });
    const actionCodeSettings = { url: window.location.origin + '/#/login', handleCodeInApp: false };
    await sendEmailVerification(cred.user, actionCodeSettings);
    await signOut(auth);
    return cred.user; 
};

export const resendVerificationEmail = async (user: any) => {
    if (isDemo || !auth) return;
    const actionCodeSettings = { url: window.location.origin + '/#/login', handleCodeInApp: false };
    await sendEmailVerification(user, actionCodeSettings);
};

export const updateUserProfile = async (uid: string, name: string, photoFile?: File): Promise<User> => {
    // DEMO FALLBACK
    if (isDemo || !auth) {
        const stored = localStorage.getItem(LS_USER_KEY);
        if (stored) {
            const u = JSON.parse(stored);
            u.displayName = name;
            // Simple file reader for demo image
            if (photoFile) {
                 const reader = new FileReader();
                 await new Promise<void>(resolve => {
                    reader.onload = () => { u.photoURL = reader.result; resolve(); };
                    reader.readAsDataURL(photoFile);
                 });
            }
            localStorage.setItem(LS_USER_KEY, JSON.stringify(u));
            return u;
        }
        throw new Error("No user");
    }

    // REAL FIREBASE
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error("No user logged in");

    let photoURL = currentUser.photoURL;

    if (photoFile) {
        const storageRef = ref(storage, `users/${uid}/profile_${Date.now()}`);
        await uploadBytesResumable(storageRef, photoFile);
        photoURL = await getDownloadURL(storageRef);
    }

    await updateProfile(currentUser, { displayName: name, photoURL: photoURL });

    return {
        uid: currentUser.uid,
        email: currentUser.email,
        displayName: name,
        photoURL: photoURL,
        emailVerified: currentUser.emailVerified
    };
};

export const logout = async () => {
    if (isDemo || !auth) {
        localStorage.removeItem(LS_USER_KEY);
        window.location.reload();
        return;
    }
    await signOut(auth);
    window.location.reload();
};

// ----- DATA SERVICE -----

export const fetchBooklets = async (ownerId?: string, publicOnly: boolean = false): Promise<Booklet[]> => {
    // DEMO FALLBACK
    if (isDemo || !db) {
        let data = getLocalBooklets();
        if (publicOnly) {
             const now = Date.now();
             data = data.filter(b => {
                 if (b.status === 'draft' || b.status === 'needs_review') return false;
                 if (b.visibility === 'unlisted') return false;
                 if (b.status === 'scheduled' && b.scheduledAt && b.scheduledAt > now) return false;
                 return true;
             });
        }
        // Demo Sort: Order first (asc), then CreatedAt (desc)
        return data.sort((a, b) => {
            const orderA = a.order ?? 9999;
            const orderB = b.order ?? 9999;
            if (orderA !== orderB) return orderA - orderB;
            return b.createdAt - a.createdAt;
        });
    }

    // REAL FIREBASE
    try {
        const bookletsRef = collection(db, "booklets");
        const q = query(bookletsRef, orderBy("order", "asc"), orderBy("createdAt", "desc"));
        
        let snapshot;
        try {
            snapshot = await getDocs(q);
        } catch (idxError) {
             const fallbackQ = query(bookletsRef, orderBy("createdAt", "desc"));
             snapshot = await getDocs(fallbackQ);
        }

        let data = snapshot.docs.map(d => {
            const raw = d.data();
            return {
                id: d.id,
                ...raw,
                // Self-heal stale signed URLs on list images too (dashboard/library cards)
                thumbnail: toPermanentUrl(raw.thumbnail),
                coverUrl: toPermanentUrl(raw.coverUrl),
                createdAt: normalizeDate(raw.createdAt),
                scheduledAt: normalizeOptionalDate(raw.scheduledAt),
                visibility: raw.visibility || 'public',
                emailGate: raw.emailGate || false,
                order: raw.order ?? 9999,
                collectedLeads: raw.collectedLeads || [], // New
                collectedEmails: raw.collectedEmails || [] // Legacy
            } as Booklet;
        });

        if (publicOnly) {
            const now = Date.now();
            data = data.filter(b => {
                 if (b.status === 'draft' || b.status === 'needs_review') return false;
                 if (b.visibility === 'unlisted') return false;
                 if (b.status === 'scheduled' && b.scheduledAt && b.scheduledAt > now) return false;
                 return true;
             });
        } else if (ownerId) {
            data = data.filter(b => b.ownerId === ownerId);
        }

        const sorted = data.sort((a, b) => {
             const orderA = a.order ?? 9999;
             const orderB = b.order ?? 9999;
             if (orderA !== orderB) return orderA - orderB;
             return b.createdAt - a.createdAt;
        });

        // Save a lightweight copy so the next visit paints instantly
        setCachedBookletList(publicOnly, sorted);

        return sorted;

    } catch (e) {
        console.error("Fetch error:", e);
        return [];
    }
};

export const updateBookletOrder = async (booklets: Booklet[]) => {
    if (isDemo || !db) {
        setLocalBooklets(booklets);
        return;
    }

    const batch = writeBatch(db);
    
    booklets.forEach((b, index) => {
        const ref = doc(db, 'booklets', b.id);
        batch.update(ref, { order: index });
    });

    await batch.commit();
};

export const getBookletById = async (id: string, isAdmin: boolean = false): Promise<Booklet | null> => {
    // DEMO FALLBACK
    if (isDemo || !db) {
        const booklets = getLocalBooklets();
        const found = booklets.find(b => b.id === id);
        return found || null;
    }

    // REAL FIREBASE
    try {
        // Heavy content (page URLs, OCR text) lives in content subdocuments so
        // list views stay tiny. Fetch everything in parallel and merge, falling
        // back to fields on the main doc for older (unmigrated) booklets.
        const [docSnap, mediaSnap, textSnap] = await Promise.all([
            getDoc(doc(db, "booklets", id)),
            getDoc(doc(db, "booklets", id, "content", "media")).catch(() => null),
            getDoc(doc(db, "booklets", id, "content", "text")).catch(() => null),
        ]);
        if (docSnap.exists()) {
             const raw = docSnap.data();
             const media = mediaSnap && mediaSnap.exists() ? mediaSnap.data() : null;
             const textData = textSnap && textSnap.exists() ? textSnap.data() : null;
             const booklet = {
                id: docSnap.id,
                ...raw,
                // Self-heal any stale signed URLs → permanent Firebase URLs (see toPermanentUrl)
                pages: toPermanentUrls(media?.pages || raw.pages),
                pagesPreview: toPermanentUrls(media?.pagesPreview || raw.pagesPreview),
                pagesMobile: toPermanentUrls(media?.pagesMobile || raw.pagesMobile),
                thumbnail: toPermanentUrl(raw.thumbnail),
                coverUrl: toPermanentUrl(raw.coverUrl),
                url: toPermanentUrl(raw.url),
                pageTexts: textData?.pageTexts || raw.pageTexts,
                createdAt: normalizeDate(raw.createdAt),
                scheduledAt: normalizeOptionalDate(raw.scheduledAt),
                visibility: raw.visibility || 'public',
                emailGate: raw.emailGate || false,
                collectedLeads: raw.collectedLeads || [], // New
                collectedEmails: raw.collectedEmails || [] // Legacy
             } as Booklet;

             // Access gating. Admins (logged-in owners) can preview anything —
             // including drafts and not-yet-live scheduled publications — so the
             // "can't view my own draft" problem is fixed. The public cannot see
             // a draft, nor a scheduled booklet before its go-live moment.
             const now = Date.now();
             const schedAt = booklet.scheduledAt || 0;
             if (!isAdmin) {
                 if (booklet.status === 'draft' || booklet.status === 'needs_review') return null;
                 if (booklet.status === 'scheduled' && schedAt && schedAt > now) return null;
             }
             // A scheduled booklet whose time has arrived goes live automatically,
             // respecting its visibility (public shows in the library; unlisted
             // stays link-only). Persist the flip so the status is clean going
             // forward (best-effort; the time filters already gate visibility).
             if (booklet.status === 'scheduled' && schedAt && schedAt <= now) {
                 booklet.status = 'published';
                 updateDoc(doc(db, 'booklets', id), { status: 'published' }).catch(() => {});
             }
             return booklet;
        }
        return null;
    } catch (e) {
        console.error("Get booklet error:", e);
        return null;
    }
};

export const uploadPDF = async (
    file: File, 
    metadata: { 
        title: string; 
        description: string; 
        ownerId: string;
        ownerName?: string;
        status: 'published' | 'draft' | 'scheduled';
        visibility: 'public' | 'unlisted';
        emailGate: boolean;
        scheduledAt?: number | null;
    },
    onProgress: (progress: number) => void,
    coverDataUrl?: string | null
  ): Promise<Booklet> => {
    
    // DEMO FALLBACK
    if (isDemo || !storage) {
        return new Promise((resolve) => {
            let p = 0;
            const interval = setInterval(() => {
                p += 20;
                onProgress(p);
                if (p >= 100) {
                    clearInterval(interval);
                    const newBooklet: Booklet = {
                        id: `local_${Date.now()}`,
                        ...metadata,
                        url: DEMO_PDF_URL, 
                        coverUrl: coverDataUrl || '', 
                        createdAt: Date.now(),
                        stats: { views: 0, uniqueReaders: 0, avgTimeSeconds: 0, shares: 0, devices: {mobile:0, desktop:0, tablet:0} },
                        hotspots: [],
                        collectedLeads: []
                    };
                    const current = getLocalBooklets();
                    setLocalBooklets([newBooklet, ...current]);
                    resolve(newBooklet);
                }
            }, 200);
        });
    }

    // REAL FIREBASE
    if (!auth || !auth.currentUser) throw new Error("Permission Denied");

    const fileId = `up_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const storageRef = ref(storage, `booklets/${fileId}`);
    const uploadTask = uploadBytesResumable(storageRef, file, {
        cacheControl: 'public, max-age=31536000',
        contentType: 'application/pdf'
    });

    return new Promise((resolve, reject) => {
        uploadTask.on('state_changed',
            (snap) => onProgress((snap.bytesTransferred / snap.totalBytes) * 100),
            (err) => reject(err),
            async () => {
                const url = await getDownloadURL(uploadTask.snapshot.ref);
                let finalCoverUrl = ""; 
                
                if (coverDataUrl) {
                    try {
                        const coverRef = ref(storage, `covers/${fileId}.jpg`);
                        await uploadString(coverRef, coverDataUrl, 'data_url');
                        finalCoverUrl = await getDownloadURL(coverRef);
                    } catch (e) {}
                }

                const finalMetadata = { ...metadata };
                if (finalMetadata.status !== 'scheduled') {
                    finalMetadata.scheduledAt = null;
                }

                const newBookletData = {
                    ...finalMetadata,
                    url,
                    createdAt: serverTimestamp(),
                    coverUrl: finalCoverUrl,
                    stats: { 
                        views: 0, 
                        uniqueReaders: 0, 
                        avgTimeSeconds: 0, 
                        shares: 0,
                        devices: { mobile: 0, desktop: 0, tablet: 0 }
                    },
                    hotspots: [],
                    collectedLeads: [],
                    order: 0
                };

                const docRef = await addDoc(collection(db, "booklets"), cleanData(newBookletData));
                
                resolve({ 
                    id: docRef.id, 
                    ...finalMetadata, 
                    url, 
                    createdAt: Date.now(),
                    coverUrl: finalCoverUrl,
                    hotspots: []
                });
            }
        );
    });
};

export const updateBooklet = async (
    bookletId: string,
    file: File | null,
    metadata: { 
        title: string; 
        description: string;
        status?: 'published' | 'draft' | 'scheduled';
        visibility?: 'public' | 'unlisted';
        emailGate?: boolean;
        scheduledAt?: number | null;
    },
    onProgress: (progress: number) => void,
    coverDataUrl?: string | null
): Promise<Booklet> => {
    
    // DEMO FALLBACK
    if (isDemo || !db) {
        const booklets = getLocalBooklets();
        const index = booklets.findIndex(b => b.id === bookletId);
        if (index === -1) throw new Error("Not found");
        
        const updated = { ...booklets[index], ...cleanData(metadata) };
        if (file) { updated.url = DEMO_PDF_URL; }
        if (coverDataUrl) { updated.coverUrl = coverDataUrl; }
        
        booklets[index] = updated;
        setLocalBooklets(booklets);
        onProgress(100);
        return updated;
    }

    // REAL FIREBASE
    const docRef = doc(db, 'booklets', bookletId);
    
    const updates: any = { ...metadata };
    if (updates.status && updates.status !== 'scheduled') {
        updates.scheduledAt = null; 
    }
    const cleanedUpdates = cleanData(updates);

    if (file) {
        const fileRef = `booklets/${bookletId}_v${Date.now()}`;
        const storageRef = ref(storage, fileRef);
        const uploadTask = uploadBytesResumable(storageRef, file, {
            cacheControl: 'public, max-age=31536000',
            contentType: 'application/pdf'
        });

        await new Promise<void>((resolve, reject) => {
            uploadTask.on('state_changed',
                (snap) => onProgress((snap.bytesTransferred / snap.totalBytes) * 100),
                (err) => reject(err),
                async () => {
                    cleanedUpdates.url = await getDownloadURL(uploadTask.snapshot.ref);
                    resolve();
                }
            );
        });
    } else {
        onProgress(100);
    }

    if (coverDataUrl && storage) {
        try {
            const coverRef = ref(storage, `covers/${bookletId}_v${Date.now()}.jpg`);
            await uploadString(coverRef, coverDataUrl, 'data_url');
            cleanedUpdates.coverUrl = await getDownloadURL(coverRef);
        } catch (e) {}
    }

    await updateDoc(docRef, cleanedUpdates);
    const finalDoc = await getDoc(docRef);
    const raw = finalDoc.data();
    return { 
        id: finalDoc.id, 
        ...raw,
        createdAt: normalizeDate(raw?.createdAt),
        scheduledAt: normalizeOptionalDate(raw?.scheduledAt),
        visibility: raw?.visibility || 'public',
        emailGate: raw?.emailGate || false
    } as Booklet;
};

export const deleteBooklet = async (id: string) => {
    if (isDemo || !db) {
        const booklets = getLocalBooklets();
        setLocalBooklets(booklets.filter(b => b.id !== id));
        return;
    }
    await deleteDoc(doc(db, "booklets", id));
};

export const saveHotspots = async (bookletId: string, hotspots: Hotspot[]): Promise<void> => {
    if (isDemo || !db) {
        const booklets = getLocalBooklets();
        const index = booklets.findIndex(b => b.id === bookletId);
        if (index !== -1) {
            booklets[index].hotspots = hotspots;
            setLocalBooklets(booklets);
        }
        return;
    }
    const docRef = doc(db, 'booklets', bookletId);
    await updateDoc(docRef, { hotspots });
};

export const saveBrandingSettings = async (settings: AppSettings) => {
    if (isDemo || !db) {
        localStorage.setItem(LS_SETTINGS_KEY, JSON.stringify(settings));
        return;
    }
    await setDoc(doc(db, 'config', 'global_settings'), cleanData(settings), { merge: true });
};

export const getBrandingSettings = async (): Promise<AppSettings> => {
    if (isDemo || !db) {
        const s = localStorage.getItem(LS_SETTINGS_KEY);
        return s ? JSON.parse(s) : {};
    }
    try {
        const snap = await getDoc(doc(db, 'config', 'global_settings'));
        return snap.exists() ? snap.data() as AppSettings : {};
    } catch (e) {
        return {};
    }
};

export const uploadLogo = async (file: File): Promise<string> => {
    if (isDemo || !storage) {
        return new Promise(resolve => {
            const r = new FileReader();
            r.onload = () => resolve(r.result as string);
            r.readAsDataURL(file);
        });
    }
    const storageRef = ref(storage, `branding/logo_${Date.now()}`);
    await uploadBytesResumable(storageRef, file);
    return getDownloadURL(storageRef);
};

export const uploadBrandLogo = async (file: File): Promise<string> => {
    if (isDemo || !storage) {
        return new Promise(resolve => {
            const r = new FileReader();
            r.onload = () => resolve(r.result as string);
            r.readAsDataURL(file);
        });
    }
    const storageRef = ref(storage, `branding/brand_${Date.now()}_${Math.random().toString(36).substring(7)}`);
    await uploadBytesResumable(storageRef, file);
    return getDownloadURL(storageRef);
};

export const recordView = async (id: string, deviceType: 'mobile' | 'desktop' | 'tablet' = 'desktop') => { 
    const storageKey = `esselle_viewed_${id}`;
    const hasViewed = localStorage.getItem(storageKey);
    const isUnique = !hasViewed;

    if (isUnique) {
        localStorage.setItem(storageKey, 'true');
    }

    // SIMULATE GEO DATA FOR DEMO/ANALYTICS (Randomly Assign Country)
    const countries = ['GB', 'US', 'DE', 'FR', 'CA', 'AU'];
    const randomCountry = countries[Math.floor(Math.random() * countries.length)];

    if (isDemo || !db) {
        const booklets = getLocalBooklets();
        const idx = booklets.findIndex(b => b.id === id);
        if (idx !== -1) {
            if (!booklets[idx].stats) booklets[idx].stats = { views:0, uniqueReaders:0, avgTimeSeconds:0, shares:0, devices:{mobile:0,desktop:0,tablet:0}, geo: {} };
            booklets[idx].stats.views++;
            if (isUnique) booklets[idx].stats.uniqueReaders++;
            
            // Devices
            if (!booklets[idx].stats.devices) booklets[idx].stats.devices = {mobile:0,desktop:0,tablet:0};
            booklets[idx].stats.devices[deviceType]++;

            // Geo
            if (!booklets[idx].stats.geo) booklets[idx].stats.geo = {};
            booklets[idx].stats.geo[randomCountry] = (booklets[idx].stats.geo[randomCountry] || 0) + 1;

            setLocalBooklets(booklets);
        }
        return;
    }

    const updates: any = { 
        'stats.views': increment(1),
        [`stats.devices.${deviceType}`]: increment(1),
        [`stats.geo.${randomCountry}`]: increment(1)
    };
    
    if (isUnique) {
        updates['stats.uniqueReaders'] = increment(1);
    }

    updateDoc(doc(db, 'booklets', id), updates).catch(() => {});
};

// Records one reading session's duration. The average shown in Analytics is
// computed as stats.totalTimeSeconds / stats.timeSessions.
export const recordReadingTime = async (id: string, seconds: number) => {
    if (!id) return;
    // Cap a single session at 30 minutes so an abandoned open tab
    // doesn't wreck the averages; ignore sub-3-second accidental opens.
    const capped = Math.min(Math.round(seconds), 1800);
    if (capped < 3) return;

    if (isDemo || !db) {
        const booklets = getLocalBooklets();
        const idx = booklets.findIndex(b => b.id === id);
        if (idx !== -1) {
            const s: any = booklets[idx].stats || { views: 0, uniqueReaders: 0, avgTimeSeconds: 0, shares: 0 };
            s.totalTimeSeconds = (s.totalTimeSeconds || 0) + capped;
            s.timeSessions = (s.timeSessions || 0) + 1;
            s.avgTimeSeconds = Math.round(s.totalTimeSeconds / s.timeSessions);
            booklets[idx].stats = s;
            setLocalBooklets(booklets);
        }
        return;
    }

    updateDoc(doc(db, 'booklets', id), {
        'stats.totalTimeSeconds': increment(capped),
        'stats.timeSessions': increment(1),
    }).catch(() => {});
};

// Records a share action (share dialog opened by a reader)
export const recordShare = async (id: string) => {
    if (!id) return;

    if (isDemo || !db) {
        const booklets = getLocalBooklets();
        const idx = booklets.findIndex(b => b.id === id);
        if (idx !== -1) {
            const s: any = booklets[idx].stats || { views: 0, uniqueReaders: 0, avgTimeSeconds: 0, shares: 0 };
            s.shares = (s.shares || 0) + 1;
            booklets[idx].stats = s;
            setLocalBooklets(booklets);
        }
        return;
    }

    updateDoc(doc(db, 'booklets', id), {
        'stats.shares': increment(1),
    }).catch(() => {});
};

export const collectEmail = async (bookletId: string, email: string, marketing: boolean = false) => {
    const lead: Lead = {
        email,
        marketing,
        date: Date.now()
    };

    if (isDemo || !db) {
        const booklets = getLocalBooklets();
        const idx = booklets.findIndex(b => b.id === bookletId);
        if (idx !== -1) {
            if (!booklets[idx].collectedLeads) booklets[idx].collectedLeads = [];
            booklets[idx].collectedLeads.push(lead);
            setLocalBooklets(booklets);
        }
        return;
    }

    const docRef = doc(db, 'booklets', bookletId);
    await updateDoc(docRef, {
        collectedLeads: arrayUnion(lead)
    });
};

export const getPluginDownloadUrl = async (): Promise<string | null> => {
    return "https://firebasestorage.googleapis.com/v0/b/esselle-publisher-b3bbf.firebasestorage.app/o/Installer%2Fcom.esselle.uploader_ID.ccx?alt=media&token=bcac058d-4dee-46d0-a9c9-5d4e5dcb0e85";
};